import { useMutation, useQueryClient } from '@tanstack/react-query';
import { serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';

import { useAuth } from '@/app/auth';
import { type WidgetId } from '@/features/dashboard/registry';
import { userWidgetDoc } from '@/lib/firestorePaths';

interface UpdateConfigInput {
  widgetId: WidgetId;
  config: Record<string, unknown>;
}

export interface DashboardWidgetMutations {
  updateConfig: (widgetId: WidgetId, config: Record<string, unknown>) => void;
  isPending: boolean;
}

/**
 * The dashboard layout is fixed, so the only per-widget write is updating its
 * settings. Writes merge `{ config, updatedAt }` into the widget's config doc
 * and invalidate the shared widgets query.
 */
export default function useDashboardWidgetMutations(): DashboardWidgetMutations {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ widgetId, config }: UpdateConfigInput) => {
      if (!uid) throw new Error('Not signed in.');
      await setDoc(userWidgetDoc(uid, widgetId), { config, updatedAt: serverTimestamp() }, { merge: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'widgets', uid] });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Failed to update widget.';
      toast.error(msg);
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'widgets', uid] });
    },
  });

  return {
    updateConfig: (widgetId, config) => mutation.mutate({ widgetId, config }),
    isPending: mutation.isPending,
  };
}
